import type { CurrentStarterInfo } from '@racemap/utilities/api-client';
import { labelFormatDistance, labelFormatVelocity } from '@racemap/utilities/formattings/units';
import type { PointWithOffsetTimeNumber } from '@racemap/utilities/functions/codec';
import { downloadBlob } from '@racemap/utilities/functions/download';
import { unitAwareVelocity } from '@racemap/utilities/functions/units';
import type { UnitType } from '@racemap/utilities/types/units';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useElementDimensions } from 'src/lib/player/utils/useElementDimensions';
import { currentAPIColors } from './consts';
import { formatDurationHHMMSS, formatHHMMSS } from './utils';

// The viewBox width (GC_W) and everything derived from it (GC_INNER_W, DATA_TRANSFORM)
// are computed per-render inside the component from the measured container size, so the
// chart can be widened via the divider without stretching. GC_W_MIN is the design width
// (= 400/320 aspect) and acts as a floor. Height-related constants stay fixed: since the
// rendered height is constant, keeping GC_H constant keeps the scale — and thus font px
// and left-axis px — constant at every width.
const GC_W_MIN = 400;
const GC_H = 320;
const GC_PAD_TOP = 24;
const GC_PAD_RIGHT = 24;
const GC_PAD_BOTTOM = 38;
const GC_PAD_LEFT = 44;
const GC_INNER_H = GC_H - GC_PAD_TOP - GC_PAD_BOTTOM;

const X_TICK_COUNT = 4;
const Y_TICK_COUNT = 5;

// Night-mode → day-mode color substitutions applied when serializing for download,
// so the SVG is always legible on white/transparent backgrounds.
const NIGHT_TO_DAY: Array<[string, string]> = [
  ['rgba(255,255,255,0.45)', 'rgba(0,0,0,0.35)'], // axisStroke
  ['rgba(255,255,255,0.6)', 'rgba(0,0,0,0.65)'], // labelFill
  ['rgba(255,255,255,0.4)', 'rgba(0,0,0,0.4)'], // noDataFill
  ['rgba(255,255,255,0.25)', 'rgba(0,0,0,0.2)'], // tickStroke
  ['#BDBDBD', '#757575'], // helperLine (CurrentAPIColorTable.helperLine)
];

type SVGDot = {
  time: number;
  offset: number;
  color: string;
  type: 'participant' | 'leader';
  isBig?: boolean; // for the current position dot, which should be larger than hover dots
  strokeColor?: string;
};

type SVGLine = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  type:
    | 'gap'
    | 'gapEx'
    | 'gapChase'
    | 'gapChaseEx'
    | 'gapAverage'
    | 'gapAverageEx'
    | 'starterSpeed'
    | 'leaderSpeed'
    | 'resultTime'
    | 'gapCalcTime'
    | 'gapDistance'
    | 'gapDistanceEx';
  width?: number;
  striped?: boolean;
};

type SVGText = {
  x: number;
  y: number;
  content: string;
  color: string;
  orientation: 'horizontal' | 'vertical';
  angle?: number;
};

type SVGRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  fillColor: string;
};

type GType =
  | 'gapDistance'
  | 'gapDistanceEx'
  | 'gap'
  | 'gapEx'
  | 'gapChase'
  | 'gapChaseEx'
  | 'gapAverage'
  | 'gapAverageEx';

const forceOfSpring = 0.35; // pulls line labels/offsets back to their fixpoint
const forceOfPush = 0.18; // pushes nearby lines/labels apart to avoid overlap
const forceOfPairSpring = 0.2; // keeps base and Ex variants visually grouped
const minDist = 0.14;
const animationStep = 0.2;
const damping = 0.88;

const G_TYPES: Array<GType> = [
  'gapDistance',
  'gapDistanceEx',
  'gap',
  'gapEx',
  'gapChase',
  'gapChaseEx',
  'gapAverage',
  'gapAverageEx',
];
const G_PAIRS: Array<[GType, GType]> = [
  ['gap', 'gapEx'],
  ['gapDistance', 'gapDistanceEx'],
  ['gapChase', 'gapChaseEx'],
  ['gapAverage', 'gapAverageEx'],
];

type GEntry = {
  fixPoint: [number, number];
  deltaX: number;
  deltaY: number;
  movesFreeIn: 'X' | 'Y';
};
type GGeometry = Record<GType, GEntry>;

type AnchorMap = Record<GType, [number, number]>;

const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

const getCurrentPoint = (entry: GEntry): [number, number] => [
  entry.fixPoint[0] + entry.deltaX,
  entry.fixPoint[1] + entry.deltaY,
];

// Builds geometry for a fresh entrance: large initial deltas so lines spring into view.
const buildInitialGeometry = (anchors: AnchorMap): GGeometry => ({
  'gapDistance': {
    fixPoint: anchors.gapDistance,
    deltaX: 0.08,
    deltaY: 0,
    movesFreeIn: 'X',
  },
  'gapDistanceEx': {
    fixPoint: anchors.gapDistanceEx,
    deltaX: 0.16,
    deltaY: 0,
    movesFreeIn: 'X',
  },
  'gap': {
    fixPoint: anchors.gap,
    deltaX: 0,
    deltaY: 0.08,
    movesFreeIn: 'Y',
  },
  'gapEx': {
    fixPoint: anchors.gapEx,
    deltaX: 0,
    deltaY: 0.16,
    movesFreeIn: 'Y',
  },
  'gapChase': {
    fixPoint: anchors.gapChase,
    deltaX: 0,
    deltaY: -0.08,
    movesFreeIn: 'Y',
  },
  'gapChaseEx': {
    fixPoint: anchors.gapChaseEx,
    deltaX: 0,
    deltaY: -0.16,
    movesFreeIn: 'Y',
  },
  'gapAverage': {
    fixPoint: anchors.gapAverage,
    deltaX: 0,
    deltaY: -0.08,
    movesFreeIn: 'Y',
  },
  'gapAverageEx': {
    fixPoint: anchors.gapAverageEx,
    deltaX: 0,
    deltaY: -0.16,
    movesFreeIn: 'Y',
  },
});

// Builds geometry keeping the current absolute screen position (fixPoint + delta) unchanged
// but swapping in new fixPoints — springs then smoothly pull to new positions.
const buildTransitionGeometry = (prev: GGeometry, newAnchors: AnchorMap): GGeometry => {
  const result: GGeometry = { ...prev };
  for (const type of G_TYPES) {
    const g = prev[type];
    const newFix = newAnchors[type];
    result[type] = {
      fixPoint: newFix,
      // Preserve absolute position: new_delta = old_delta + (old_fixPoint - new_fixPoint)
      deltaX:
        g.movesFreeIn === 'X' ? clamp(g.deltaX + (g.fixPoint[0] - newFix[0]), -0.45, 0.45) : 0,
      deltaY:
        g.movesFreeIn === 'Y' ? clamp(g.deltaY + (g.fixPoint[1] - newFix[1]), -0.45, 0.45) : 0,
      movesFreeIn: g.movesFreeIn,
    };
  }
  return result;
};

export function calcNewDeltas(geometry: GGeometry): GGeometry {
  // Each entry is pulled to its fixpoint and pushed away from close neighbors.
  const forces: Record<GType, [number, number]> = {
    gapDistance: [0, 0],
    gapDistanceEx: [0, 0],
    gap: [0, 0],
    gapEx: [0, 0],
    gapChase: [0, 0],
    gapChaseEx: [0, 0],
    gapAverage: [0, 0],
    gapAverageEx: [0, 0],
  };
  for (const type1 of G_TYPES) {
    const g1 = geometry[type1];
    const [x1, y1] = getCurrentPoint(g1);
    let fx = -forceOfSpring * g1.deltaX;
    let fy = -forceOfSpring * g1.deltaY;
    for (const type2 of G_TYPES) {
      if (type1 === type2) continue;
      const g2 = geometry[type2];
      const [x2, y2] = getCurrentPoint(g2);
      const dx = x1 - x2;
      const dy = y1 - y2;
      const distSq = dx * dx + dy * dy;
      if (distSq < minDist * minDist) {
        const dist = Math.sqrt(distSq) || 0.01;
        const push = (forceOfPush * (minDist - dist)) / dist;
        fx += push * dx;
        fy += push * dy;
      }
    }
    forces[type1] = [fx, fy];
  }

  // Pair springs keep "base" and "Ex" elements moving together.
  for (const [left, right] of G_PAIRS) {
    const l = geometry[left];
    const r = geometry[right];
    const [lx, ly] = getCurrentPoint(l);
    const [rx, ry] = getCurrentPoint(r);
    const desiredDx = r.fixPoint[0] - l.fixPoint[0];
    const desiredDy = r.fixPoint[1] - l.fixPoint[1];
    const errX = rx - lx - desiredDx;
    const errY = ry - ly - desiredDy;
    forces[left][0] += forceOfPairSpring * errX;
    forces[left][1] += forceOfPairSpring * errY;
    forces[right][0] -= forceOfPairSpring * errX;
    forces[right][1] -= forceOfPairSpring * errY;
  }

  const newGeometry: GGeometry = {
    gapDistance: geometry.gapDistance,
    gapDistanceEx: geometry.gapDistanceEx,
    gap: geometry.gap,
    gapEx: geometry.gapEx,
    gapChase: geometry.gapChase,
    gapChaseEx: geometry.gapChaseEx,
    gapAverage: geometry.gapAverage,
    gapAverageEx: geometry.gapAverageEx,
  };
  for (const type of G_TYPES) {
    const g = geometry[type];
    const [fx, fy] = forces[type];
    let nextDeltaX = g.deltaX;
    let nextDeltaY = g.deltaY;
    if (g.movesFreeIn === 'X') {
      nextDeltaX = (g.deltaX + fx * animationStep) * damping;
      nextDeltaX = clamp(nextDeltaX, -0.45, 0.45);
      nextDeltaY = 0;
    } else {
      nextDeltaY = (g.deltaY + fy * animationStep) * damping;
      nextDeltaY = clamp(nextDeltaY, -0.45, 0.45);
      nextDeltaX = 0;
    }
    newGeometry[type] = {
      fixPoint: g.fixPoint,
      deltaX: nextDeltaX,
      deltaY: nextDeltaY,
      movesFreeIn: g.movesFreeIn,
    };
  }
  return newGeometry;
}

function geometryMotion(geometry: GGeometry): number {
  let maxDelta = 0;
  for (const type of G_TYPES) {
    const g = geometry[type];
    maxDelta = Math.max(maxDelta, Math.abs(g.deltaX), Math.abs(g.deltaY));
  }
  return maxDelta;
}

export interface GapsChartProps {
  starterCurrent: CurrentStarterInfo | null;
  leaderCurrent: CurrentStarterInfo | null;
  resultTimeInMillis: number | null;
  rawStarterPoints: Array<PointWithOffsetTimeNumber>;
  rawLeaderPoints: Array<PointWithOffsetTimeNumber>;
  avgSpeedWindowSeconds: number;
  unitType: UnitType;
  isDayMode?: boolean;
  eventName?: string;
}

export const GapsChart: FC<GapsChartProps> = ({
  starterCurrent,
  leaderCurrent,
  resultTimeInMillis,
  rawStarterPoints,
  rawLeaderPoints,
  avgSpeedWindowSeconds,
  unitType,
  isDayMode = false,
  eventName,
}) => {
  const colors = currentAPIColors(isDayMode);
  const bgFill = isDayMode ? '#f0f0f0' : '#343332';
  const axisStroke = isDayMode ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.45)';
  const labelFill = isDayMode ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.6)';
  const noDataFill = isDayMode ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)';
  const tickStroke = isDayMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.25)';

  // Measure the container and derive the viewBox width so its aspect ratio matches the
  // container. With a uniform scale the SVG fills the panel without stretching, and because
  // the rendered height is fixed the scale stays constant — fonts and the left axis keep the
  // same pixel size/offset at any width. Widening only grows GC_W/GC_INNER_W (the plot area).
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth, height: containerHeight } = useElementDimensions(containerRef);
  const GC_W =
    containerHeight > 0 ? Math.max(GC_W_MIN, (containerWidth / containerHeight) * GC_H) : GC_W_MIN;
  const GC_INNER_W = GC_W - GC_PAD_LEFT - GC_PAD_RIGHT;
  // Maps unit [0,1]×[0,1] (origin = plot bottom-left, y increases upward) to SVG pixel plot area.
  const DATA_TRANSFORM = `translate(${GC_PAD_LEFT}, ${GC_PAD_TOP + GC_INNER_H}) scale(${GC_INNER_W}, ${-GC_INNER_H})`;

  const [geometry, setGeometry] = useState<GGeometry | null>(null);
  const timeInMillis =
    starterCurrent != null ? new Date(starterCurrent.current.time).getTime() : null;

  const lTimeInMillis =
    leaderCurrent != null ? new Date(leaderCurrent.current.time).getTime() : null;

  const gapCalcTimeInMillis =
    starterCurrent != null && starterCurrent.current.gapCalcTime != null
      ? new Date(starterCurrent.current.gapCalcTime).getTime()
      : null;
  const leaderDefinesGapCalcTime: boolean =
    lTimeInMillis != null && gapCalcTimeInMillis != null && lTimeInMillis === gapCalcTimeInMillis;

  const allMillis: Array<number> = [];
  const allOffsets: Array<number> = [];

  if (starterCurrent != null && timeInMillis != null && resultTimeInMillis != null) {
    allMillis.push(timeInMillis);
    if (leaderCurrent != null) {
      if (leaderCurrent.current.fromStart != null) {
        allOffsets.push(leaderCurrent.current.fromStart);
        if (starterCurrent.current.gapDistance != null) {
          allOffsets.push(leaderCurrent.current.fromStart - starterCurrent.current.gapDistance);
        }
      }
    }
    if (resultTimeInMillis != null) allMillis.push(resultTimeInMillis);
    if (gapCalcTimeInMillis != null) allMillis.push(gapCalcTimeInMillis);
    if (starterCurrent.current.gap != null)
      allMillis.push(timeInMillis - 1000 * starterCurrent.current.gap);
    if (starterCurrent.current.gapChase != null)
      allMillis.push(timeInMillis + 1000 * starterCurrent.current.gapChase);
    if (starterCurrent.current.gapAverage != null)
      allMillis.push(timeInMillis - 1000 * starterCurrent.current.gapAverage);
    if (starterCurrent.current.gapEx != null)
      allMillis.push(resultTimeInMillis - 1000 * starterCurrent.current.gapEx);
    if (starterCurrent.current.gapChaseEx != null)
      allMillis.push(resultTimeInMillis + 1000 * starterCurrent.current.gapChaseEx);
    if (starterCurrent.current.gapAverageEx != null)
      allMillis.push(resultTimeInMillis - 1000 * starterCurrent.current.gapAverageEx);
    if (starterCurrent.current.fromStart != null) allOffsets.push(starterCurrent.current.fromStart);
    if (starterCurrent.current.fromStartEx != null)
      allOffsets.push(starterCurrent.current.fromStartEx);

    if (starterCurrent.current.fromStart != null && starterCurrent.current.gapDistance != null)
      allOffsets.push(starterCurrent.current.fromStart + starterCurrent.current.gapDistance);

    if (starterCurrent.current.fromStartEx != null && starterCurrent.current.gapDistanceEx != null)
      allOffsets.push(starterCurrent.current.fromStartEx + starterCurrent.current.gapDistanceEx);
  }

  let scales: {
    minTime: number;
    maxTime: number;
    minOffset: number;
    maxOffset: number;
    timeRange: number;
    offsetRange: number;
    toX: (time: number) => number;
    toY: (offset: number) => number;
  } | null = null;
  if (allMillis.length > 0 && allOffsets.length > 0) {
    let minTime = Math.min(...allMillis);
    let maxTime = Math.max(...allMillis);
    let minOffset = Math.min(...allOffsets);
    let maxOffset = Math.max(...allOffsets);
    let timeRange = maxTime - minTime;
    let offsetRange = maxOffset - minOffset;
    minTime = minTime - 0.1 * timeRange;
    maxTime = maxTime + 0.1 * timeRange;
    minOffset = minOffset - 0.3 * offsetRange;
    maxOffset = maxOffset + 0.3 * offsetRange;
    // update ranges after padding
    timeRange = maxTime - minTime;
    offsetRange = maxOffset - minOffset;
    // ensure non-zero ranges so toX/toY never divide by zero
    if (timeRange === 0) {
      minTime -= 60_000;
      maxTime += 60_000;
      timeRange = maxTime - minTime;
    }
    if (offsetRange === 0) {
      minOffset -= 100;
      maxOffset += 100;
      offsetRange = maxOffset - minOffset;
    }
    const toX = (time: number) => (time - minTime) / timeRange;
    const toY = (offset: number) => (offset - minOffset) / offsetRange;
    scales = { minTime, maxTime, minOffset, maxOffset, offsetRange, timeRange, toX, toY };
  }

  const dots: Array<SVGDot> = [];
  const lines: Array<SVGLine> = [];
  const texts: Array<SVGText> = [];
  const rects: Array<SVGRect> = [];

  const anchors = useMemo<AnchorMap | null>(() => {
    if (scales == null || starterCurrent == null || resultTimeInMillis == null) return null;
    const computed: Partial<AnchorMap> = {};

    if (gapCalcTimeInMillis != null && starterCurrent.current.fromStart != null) {
      if (starterCurrent.current.gapDistance != null) {
        const start =
          leaderDefinesGapCalcTime && leaderCurrent?.current.fromStart != null
            ? leaderCurrent.current.fromStart
            : starterCurrent.current.fromStart;
        const x1 = scales.toX(gapCalcTimeInMillis);
        const y1 = scales.toY(start);
        const y2 = scales.toY(
          leaderDefinesGapCalcTime
            ? start - starterCurrent.current.gapDistance
            : start + starterCurrent.current.gapDistance,
        );
        const dx = 0.08;
        computed.gapDistance = [x1 + dx, 0.5 * (y1 + y2)];
      }

      if (starterCurrent.current.gap != null) {
        const start =
          leaderDefinesGapCalcTime &&
          leaderCurrent?.current.fromStart != null &&
          starterCurrent.current.gapDistance != null
            ? leaderCurrent.current.fromStart - starterCurrent.current.gapDistance
            : starterCurrent.current.fromStart;
        const x1 = scales.toX(gapCalcTimeInMillis);
        const x2 = scales.toX(gapCalcTimeInMillis - 1000 * starterCurrent.current.gap);
        const y1 = scales.toY(start);
        const dy = 0.08;
        computed.gap = [0.5 * (x1 + x2), y1 - dy];
      }

      if (starterCurrent.current.gapChase != null && starterCurrent.current.gapDistance != null) {
        const start =
          leaderDefinesGapCalcTime &&
          leaderCurrent?.current.fromStart != null &&
          starterCurrent.current.gapDistance != null
            ? leaderCurrent.current.fromStart
            : starterCurrent.current.fromStart + starterCurrent.current.gapDistance;
        const x1 = scales.toX(gapCalcTimeInMillis);
        const x2 = scales.toX(gapCalcTimeInMillis + 1000 * starterCurrent.current.gapChase);
        const y1 = scales.toY(start);
        const dy = -0.08;
        computed.gapChase = [0.5 * (x1 + x2), y1 - dy];
      }

      if (starterCurrent.current.gapAverage != null) {
        const start =
          leaderDefinesGapCalcTime &&
          leaderCurrent?.current.fromStart != null &&
          starterCurrent.current.gapDistance != null
            ? leaderCurrent.current.fromStart - starterCurrent.current.gapDistance
            : starterCurrent.current.fromStart;
        const x1 = scales.toX(gapCalcTimeInMillis);
        const x2 = scales.toX(gapCalcTimeInMillis - 1000 * starterCurrent.current.gapAverage);
        const y1 = scales.toY(start);
        const dy = -0.08;
        computed.gapAverage = [0.5 * (x1 + x2), y1 - dy];
      }
    }

    if (
      starterCurrent.current.fromStartEx != null &&
      starterCurrent.current.gapEx != null &&
      starterCurrent.current.fromStart != null
    ) {
      const x1 = scales.toX(resultTimeInMillis);
      const x2 = scales.toX(resultTimeInMillis - 1000 * starterCurrent.current.gapEx);
      const y1 = scales.toY(starterCurrent.current.fromStartEx);
      const dy = 0.16;
      computed.gapEx = [0.5 * (x1 + x2), y1 - dy];
    }

    if (
      starterCurrent.current.fromStartEx != null &&
      starterCurrent.current.gapDistanceEx != null
    ) {
      const x1 = scales.toX(resultTimeInMillis);
      const y1 = scales.toY(starterCurrent.current.fromStartEx);
      const y2 = scales.toY(
        starterCurrent.current.fromStartEx + starterCurrent.current.gapDistanceEx,
      );
      const dx =
        gapCalcTimeInMillis != null
          ? 0.16 + scales.toX(gapCalcTimeInMillis) - scales.toX(resultTimeInMillis)
          : 0.16;
      computed.gapDistanceEx = [x1 + dx, 0.5 * (y1 + y2)];
    }

    if (
      leaderCurrent != null &&
      leaderCurrent.current.fromStartEx != null &&
      leaderCurrent.current.fromStart != null &&
      starterCurrent.current.gapChaseEx != null &&
      starterCurrent.current.fromStart != null
    ) {
      const x1 = scales.toX(resultTimeInMillis);
      const x2 = scales.toX(resultTimeInMillis + 1000 * starterCurrent.current.gapChaseEx);
      const y1 = scales.toY(leaderCurrent.current.fromStartEx);
      const dy = -0.16;
      computed.gapChaseEx = [0.5 * (x1 + x2), y1 - dy];
    }

    if (
      starterCurrent.current.fromStartEx != null &&
      starterCurrent.current.gapAverageEx != null &&
      starterCurrent.current.fromStart != null
    ) {
      const x1 = scales.toX(resultTimeInMillis);
      const x2 = scales.toX(resultTimeInMillis - 1000 * starterCurrent.current.gapAverageEx);
      const y1 = scales.toY(starterCurrent.current.fromStartEx);
      const dy = -0.16;
      computed.gapAverageEx = [0.5 * (x1 + x2), y1 - dy];
    }

    if (
      computed.gap == null ||
      computed.gapEx == null ||
      computed.gapDistance == null ||
      computed.gapDistanceEx == null ||
      computed.gapChase == null ||
      computed.gapChaseEx == null ||
      computed.gapAverage == null ||
      computed.gapAverageEx == null
    ) {
      return null;
    }
    return computed as AnchorMap;
  }, [
    gapCalcTimeInMillis,
    leaderCurrent,
    leaderDefinesGapCalcTime,
    resultTimeInMillis,
    scales,
    starterCurrent,
  ]);

  // Key changes only when anchor positions change — not on every timestamp tick.
  // This prevents needless animation resets while scrubbing.
  const geometryResetKey = useMemo(() => {
    if (anchors == null) return 'no-anchors';
    return G_TYPES.map(
      (type) => `${type}:${anchors[type][0].toFixed(4)},${anchors[type][1].toFixed(4)}`,
    ).join('|');
  }, [anchors]);

  // Store anchors and last settled geometry in refs so effects don't re-run on object identity changes.
  const svgRef = useRef<SVGSVGElement>(null);
  const [hovered, setHovered] = useState(false);
  const anchorsRef = useRef<AnchorMap | null>(null);
  anchorsRef.current = anchors;
  const prevGeomRef = useRef<GGeometry | null>(null);

  useEffect(() => {
    const currentAnchors = anchorsRef.current;
    if (currentAnchors == null) return;

    // If we already have a settled geometry, transition smoothly by preserving
    // current absolute screen positions — springs then pull to the new fixpoints.
    // Otherwise use buildInitialGeometry for the first-time entrance effect.
    const prev = prevGeomRef.current;
    const startGeometry =
      prev != null
        ? buildTransitionGeometry(prev, currentAnchors)
        : buildInitialGeometry(currentAnchors);
    setGeometry(startGeometry);

    let rafId = 0;
    let running = true;

    const step = (geom: GGeometry) => {
      if (!running) return;
      const next = calcNewDeltas(geom);
      setGeometry(next);
      // Always keep prevGeomRef current so that a cleanup mid-animation
      // still gives the next transition a valid starting position.
      prevGeomRef.current = next;
      if (geometryMotion(next) > 0.003) {
        rafId = window.requestAnimationFrame(() => step(next));
      }
    };

    rafId = window.requestAnimationFrame(() => step(startGeometry));

    return () => {
      running = false;
      window.cancelAnimationFrame(rafId);
    };
    // geometryResetKey is a stable string – value-equal across renders when data doesn't change.
    // anchors/geometry are read via refs to avoid re-running this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometryResetKey]);

  if (scales != null) {
    if (resultTimeInMillis != null) {
      lines.push({
        x1: scales.toX(resultTimeInMillis),
        y1: scales.toY(scales.minOffset),
        x2: scales.toX(resultTimeInMillis),
        y2: scales.toY(scales.maxOffset),
        color: '#aaa',
        type: 'resultTime',
        striped: true,
      });
    }
    if (gapCalcTimeInMillis != null) {
      lines.push({
        x1: scales.toX(gapCalcTimeInMillis),
        y1: scales.toY(scales.minOffset),
        x2: scales.toX(gapCalcTimeInMillis),
        y2: scales.toY(scales.maxOffset),
        color: '#aaa',
        type: 'gapCalcTime',
      });
    }

    if (starterCurrent != null) {
      const timeInMs = new Date(starterCurrent.current.time).getTime();
      if (starterCurrent.current.fromStart != null) {
        // here the stater is located as a blue dot
        dots.push({
          time: scales.toX(timeInMs),
          offset: scales.toY(starterCurrent.current.fromStart),
          color: colors.participant,
          strokeColor: colors.participantStroke,
          isBig: true,
          type: 'participant',
        });
        if (starterCurrent.current.speedSt != null) {
          const x1 = scales.toX(scales.minTime);
          const y1 = scales.toY(
            starterCurrent.current.fromStart -
              ((timeInMs - scales.minTime) / 1000) * starterCurrent.current.speedSt,
          );
          const x2 = scales.toX(scales.maxTime);
          const y2 = scales.toY(
            starterCurrent.current.fromStart +
              ((scales.maxTime - timeInMs) / 1000) * starterCurrent.current.speedSt,
          );

          // the speedSt of the starter at last known time with avgSpeedWindow
          lines.push({
            x1,
            y1,
            x2,
            y2,
            color: colors.participant,
            type: 'starterSpeed',
          });

          // Clip speedSt line to plot area [0,1]×[0,1] in normalised coords (x is always [0,1])
          const yDelta = y2 - y1;
          let tStart = 0;
          let tEnd = 1;
          if (yDelta !== 0) {
            if (y1 < 0) tStart = Math.max(tStart, -y1 / yDelta);
            if (y1 > 1) tStart = Math.max(tStart, (1 - y1) / yDelta);
            if (y2 > 1) tEnd = Math.min(tEnd, (1 - y1) / yDelta);
            if (y2 < 0) tEnd = Math.min(tEnd, -y1 / yDelta);
          }
          const tLabel = tStart + 0.9 * (tEnd - tStart);
          const xLabel = x1 + tLabel * (x2 - x1);
          const yLabel = y1 + tLabel * yDelta;
          const slopeNorm =
            (starterCurrent.current.speedSt * scales.timeRange) / 1000 / scales.offsetRange;
          const lineAngleDeg = Math.atan2(-slopeNorm * GC_INNER_H, GC_INNER_W) * (180 / Math.PI);
          texts.push({
            x: xLabel,
            y: yLabel,
            content: `${labelFormatVelocity(unitAwareVelocity(3.6 * starterCurrent.current.speedSt, unitType), unitType, 1)}`,
            color: colors.participant,
            orientation: 'horizontal',
            angle: lineAngleDeg,
          });
        }

        if (gapCalcTimeInMillis != null) {
          // the window within average sppedSt is calculated, as a shaded rectangle
          rects.push({
            x: scales.toX(timeInMs - avgSpeedWindowSeconds * 1000),
            y: 0,
            width: (avgSpeedWindowSeconds * 1000) / scales.timeRange,
            height: scales.toY(starterCurrent.current.fromStart),
            fillColor: `${colors.participant}4d`,
          });

          if (starterCurrent.current.gapDistance != null) {
            const start =
              leaderDefinesGapCalcTime && leaderCurrent?.current.fromStart != null
                ? leaderCurrent.current.fromStart
                : starterCurrent.current.fromStart;
            const x1 = scales.toX(gapCalcTimeInMillis);
            const y1 = scales.toY(start);
            const y2 = scales.toY(
              leaderDefinesGapCalcTime
                ? start - starterCurrent.current.gapDistance
                : start + starterCurrent.current.gapDistance,
            );
            const dx = 0.08;
            const anchor = anchors?.gapDistance;
            const current = geometry?.gapDistance;
            const xAnimated =
              anchor != null && current != null
                ? clamp(anchor[0] + current.deltaX, Math.max(0.05, x1 + 0.015), 0.97)
                : x1 + dx;

            // its the distance in meters to the leader/reference
            lines.push({
              x1: x1,
              y1: y2,
              x2: xAnimated,
              y2: y2,
              color: colors.helperLine,
              type: 'gapDistance',
              striped: true,
            });
            lines.push({
              x1: xAnimated,
              y1,
              x2: xAnimated,
              y2,
              color: colors.gapDistance,
              type: 'gapDistance',
              width: 2,
            });
            texts.push({
              x: xAnimated,
              y: (y1 + y2) / 2,
              content: `gapDistance: ${labelFormatDistance(starterCurrent.current.gapDistance, unitType, 1)}`,
              color: colors.gapDistance,
              orientation: 'vertical',
            });
            lines.push({
              x1: x1,
              y1,
              x2: xAnimated,
              y2: y1,
              color: colors.helperLine,
              type: 'gapDistance',
              striped: true,
            });
          }

          if (starterCurrent.current.gap != null) {
            const start =
              leaderDefinesGapCalcTime &&
              leaderCurrent?.current.fromStart != null &&
              starterCurrent.current.gapDistance != null
                ? leaderCurrent.current.fromStart - starterCurrent.current.gapDistance
                : starterCurrent.current.fromStart;
            const x1 = scales.toX(gapCalcTimeInMillis);
            const x2 = scales.toX(gapCalcTimeInMillis - 1000 * starterCurrent.current.gap);
            const y1 = scales.toY(start);
            const dy = 0.05;
            const anchor = anchors?.gap;
            const current = geometry?.gap;
            const labelY =
              anchor != null && current != null
                ? clamp(anchor[1] + current.deltaY, 0.03, 0.97)
                : y1 - dy;
            lines.push({
              x1,
              y1: y1,
              x2: x1,
              y2: labelY,
              color: colors.helperLine,
              type: 'gap',
              striped: true,
            });
            lines.push({
              x1,
              y1: labelY,
              x2,
              y2: labelY,
              color: colors.gap,
              width: 2,
              type: 'gap',
            });
            lines.push({
              x1,
              y1,
              x2,
              y2: y1,
              color: colors.helperLine,
              width: 1,
              type: 'gap',
            });
            texts.push({
              x: 0.5 * (x1 + x2),
              y: labelY,
              content: `gap: ${formatDurationHHMMSS(starterCurrent.current.gap)}`,
              color: colors.gap,
              orientation: 'horizontal',
            });
            lines.push({
              x1: x2,
              y1: y1,
              x2: x2,
              y2: labelY,
              color: colors.helperLine,
              striped: true,
              type: 'gap',
            });
          }

          if (
            starterCurrent.current.gapChase != null &&
            starterCurrent.current.gapDistance != null
          ) {
            const start =
              leaderDefinesGapCalcTime &&
              leaderCurrent?.current.fromStart != null &&
              starterCurrent.current.gapDistance != null
                ? leaderCurrent.current.fromStart
                : starterCurrent.current.fromStart + starterCurrent.current.gapDistance;

            const x1 = scales.toX(gapCalcTimeInMillis);
            const x2 = scales.toX(gapCalcTimeInMillis + 1000 * starterCurrent.current.gapChase);
            const y1 = scales.toY(start);
            const dy = -0.08;
            const anchor = anchors?.gapChase;
            const current = geometry?.gapChase;
            const labelY =
              anchor != null && current != null
                ? clamp(anchor[1] + current.deltaY, 0.03, 0.97)
                : y1 - dy;
            lines.push({
              x1,
              y1: y1,
              x2: x1,
              y2: labelY,
              color: colors.helperLine,
              type: 'gapChase',
              striped: true,
            });
            lines.push({
              x1,
              y1: labelY,
              x2,
              y2: labelY,
              color: colors.gapChase,
              width: 2,
              type: 'gapChase',
            });
            lines.push({
              x1,
              y1,
              x2,
              y2: y1,
              color: colors.helperLine,
              width: 1,
              type: 'gapChase',
            });
            texts.push({
              x: 0.5 * (x1 + x2),
              y: labelY,
              content: `gapChase: ${formatDurationHHMMSS(starterCurrent.current.gapChase)}`,
              color: colors.gapChase,
              orientation: 'horizontal',
            });
            lines.push({
              x1: x2,
              y1: y1,
              x2: x2,
              y2: labelY,
              color: colors.helperLine,
              striped: true,
              type: 'gapChase',
            });
          }

          if (starterCurrent.current.gapAverage != null) {
            const start =
              leaderDefinesGapCalcTime &&
              leaderCurrent?.current.fromStart != null &&
              starterCurrent.current.gapDistance != null
                ? leaderCurrent.current.fromStart - starterCurrent.current.gapDistance
                : starterCurrent.current.fromStart;
            const x1 = scales.toX(gapCalcTimeInMillis);
            const x2 = scales.toX(gapCalcTimeInMillis - 1000 * starterCurrent.current.gapAverage);
            const y1 = scales.toY(start);
            const dy = -0.05;
            const anchor = anchors?.gapAverage;
            const current = geometry?.gapAverage;
            const labelY =
              anchor != null && current != null
                ? clamp(anchor[1] + current.deltaY, 0.03, 0.97)
                : y1 - dy;
            lines.push({
              x1,
              y1: y1,
              x2: x1,
              y2: labelY,
              color: colors.helperLine,
              type: 'gapAverage',
              striped: true,
            });
            lines.push({
              x1,
              y1: labelY,
              x2,
              y2: labelY,
              color: colors.gapAverage,
              width: 2,
              type: 'gapAverage',
            });
            lines.push({
              x1,
              y1,
              x2,
              y2: y1,
              color: colors.helperLine,
              width: 1,
              type: 'gapAverage',
            });
            texts.push({
              x: 0.5 * (x1 + x2),
              y: labelY,
              content: `gapAverage: ${formatDurationHHMMSS(starterCurrent.current.gapAverage)}`,
              color: colors.gapAverage,
              orientation: 'horizontal',
            });
            lines.push({
              x1: x2,
              y1: y1,
              x2: x2,
              y2: labelY,
              color: colors.helperLine,
              striped: true,
              type: 'gapAverage',
            });
          }
        }
      }

      if (resultTimeInMillis != null) {
        if (starterCurrent.current.fromStartEx != null) {
          if (starterCurrent.current.gapEx != null && starterCurrent.current.fromStart) {
            const x1 = scales.toX(resultTimeInMillis);
            const x2 = scales.toX(resultTimeInMillis - 1000 * starterCurrent.current.gapEx);
            const y1 = scales.toY(starterCurrent.current.fromStartEx);
            const dy =
              0.1 +
              scales.toY(starterCurrent.current.fromStartEx) -
              scales.toY(starterCurrent.current.fromStart);
            const anchor = anchors?.gapEx;
            const current = geometry?.gapEx;
            const labelY =
              anchor != null && current != null
                ? clamp(anchor[1] + current.deltaY, 0.03, 0.97)
                : y1 - dy;

            lines.push({
              x1,
              y1: y1,
              x2: x1,
              y2: labelY,
              color: colors.helperLine,
              type: 'gapEx',
              striped: true,
            });
            lines.push({
              x1,
              y1: labelY,
              x2,
              y2: labelY,
              color: colors.gapEx,
              width: 2,
              type: 'gapEx',
            });
            texts.push({
              x: 0.5 * (x1 + x2),
              y: labelY,
              content: `gapEx: ${formatDurationHHMMSS(starterCurrent.current.gapEx)}`,
              color: colors.gapEx,
              orientation: 'horizontal',
            });
            lines.push({
              x1: x2,
              y1: y1,
              x2: x2,
              y2: labelY,
              color: colors.helperLine,
              striped: true,
              type: 'gapEx',
            });
          }

          if (starterCurrent.current.gapDistanceEx != null && gapCalcTimeInMillis != null) {
            const x1 = scales.toX(resultTimeInMillis);
            const y1 = scales.toY(starterCurrent.current.fromStartEx);
            const y2 = scales.toY(
              starterCurrent.current.fromStartEx + starterCurrent.current.gapDistanceEx,
            );
            const dx = 0.1 + scales.toX(gapCalcTimeInMillis) - scales.toX(resultTimeInMillis);
            const anchor = anchors?.gapDistanceEx;
            const current = geometry?.gapDistanceEx;
            const xAnimated =
              anchor != null && current != null
                ? clamp(anchor[0] + current.deltaX, Math.max(0.05, x1 + 0.015), 0.97)
                : x1 + dx;

            // its the distance in meters to the leader/reference
            lines.push({
              x1: x1,
              y1: y2,
              x2: xAnimated,
              y2: y2,
              color: colors.helperLine,
              type: 'gapDistanceEx',
              striped: true,
            });
            lines.push({
              x1: xAnimated,
              y1,
              x2: xAnimated,
              y2,
              color: colors.gapDistanceEx,
              type: 'gapDistanceEx',
              width: 2,
            });
            texts.push({
              x: xAnimated,
              y: (y1 + y2) / 2,
              content: `gapDistanceEx: ${labelFormatDistance(starterCurrent.current.gapDistanceEx, unitType)}`,
              color: colors.gapDistanceEx,
              orientation: 'vertical',
            });
            lines.push({
              x1: x1,
              y1,
              x2: xAnimated,
              y2: y1,
              color: colors.helperLine,
              type: 'gapDistanceEx',
              striped: true,
            });
          }

          if (
            leaderCurrent != null &&
            starterCurrent.current.gapChaseEx != null &&
            leaderCurrent.current.fromStart != null &&
            leaderCurrent.current.fromStartEx != null
          ) {
            const x1 = scales.toX(resultTimeInMillis);
            const x2 = scales.toX(resultTimeInMillis + 1000 * starterCurrent.current.gapChaseEx);
            const y1 = scales.toY(leaderCurrent.current.fromStartEx);
            const dy = -0.14;
            const anchor = anchors?.gapChaseEx;
            const current = geometry?.gapChaseEx;
            const labelY =
              anchor != null && current != null
                ? clamp(anchor[1] + current.deltaY, 0.03, 0.97)
                : y1 - dy;
            lines.push({
              x1,
              y1: y1,
              x2: x1,
              y2: labelY,
              color: colors.helperLine,
              type: 'gapChaseEx',
              striped: true,
            });
            lines.push({
              x1,
              y1: labelY,
              x2,
              y2: labelY,
              color: colors.gapChaseEx,
              width: 2,
              type: 'gapChaseEx',
            });
            texts.push({
              x: 0.5 * (x1 + x2),
              y: labelY,
              content: `gapChaseEx: ${formatDurationHHMMSS(starterCurrent.current.gapChaseEx)}`,
              color: colors.gapChaseEx,
              orientation: 'horizontal',
            });
            lines.push({
              x1: x2,
              y1: y1,
              x2: x2,
              y2: labelY,
              color: colors.helperLine,
              striped: true,
              type: 'gapChaseEx',
            });
          }

          if (
            starterCurrent.current.gapAverageEx != null &&
            starterCurrent.current.fromStart != null
          ) {
            const x1 = scales.toX(resultTimeInMillis);
            const x2 = scales.toX(resultTimeInMillis - 1000 * starterCurrent.current.gapAverageEx);
            const y1 = scales.toY(starterCurrent.current.fromStartEx);
            const dy =
              -0.1 +
              scales.toY(starterCurrent.current.fromStartEx) -
              scales.toY(starterCurrent.current.fromStart);
            const anchor = anchors?.gapAverageEx;
            const current = geometry?.gapAverageEx;
            const labelY =
              anchor != null && current != null
                ? clamp(anchor[1] + current.deltaY, 0.03, 0.97)
                : y1 - dy;
            lines.push({
              x1,
              y1: y1,
              x2: x1,
              y2: labelY,
              color: colors.helperLine,
              type: 'gapAverageEx',
              striped: true,
            });
            lines.push({
              x1,
              y1: labelY,
              x2,
              y2: labelY,
              color: colors.gapAverageEx,
              width: 2,
              type: 'gapAverageEx',
            });
            texts.push({
              x: 0.5 * (x1 + x2),
              y: labelY,
              content: `gapAverageEx: ${formatDurationHHMMSS(starterCurrent.current.gapAverageEx)}`,
              color: colors.gapAverageEx,
              orientation: 'horizontal',
            });
            lines.push({
              x1: x2,
              y1: y1,
              x2: x2,
              y2: labelY,
              color: colors.helperLine,
              striped: true,
              type: 'gapAverageEx',
            });
          }
        }
      }
    }

    for (const p of rawStarterPoints) {
      if (p.time < scales.minTime) continue;
      dots.push({
        time: scales.toX(p.time),
        offset: scales.toY(p.offset),
        color: colors.participant,
        type: 'participant',
      });
    }

    if (leaderCurrent != null) {
      const timeInMs = new Date(leaderCurrent.current.time).getTime();
      if (leaderCurrent.current.fromStart != null) {
        dots.push({
          time: scales.toX(timeInMs),
          offset: scales.toY(leaderCurrent.current.fromStart),
          color: colors.leader,
          strokeColor: colors.leaderStroke,
          isBig: true,
          type: 'leader',
        });
        if (leaderCurrent.current.speedSt != null) {
          const x1 = scales.toX(scales.minTime);
          const x2 = scales.toX(scales.maxTime);
          const y1 = scales.toY(
            leaderCurrent.current.fromStart -
              ((timeInMs - scales.minTime) / 1000) * leaderCurrent.current.speedSt,
          );
          const y2 = scales.toY(
            leaderCurrent.current.fromStart +
              ((scales.maxTime - timeInMs) / 1000) * leaderCurrent.current.speedSt,
          );

          // the speedSt of the starter at last knon time with avgSpeedWindow
          lines.push({
            x1,
            y1,
            x2,
            y2,
            color: colors.leader,
            type: 'leaderSpeed',
          });
          // Clip speedSt line to plot area [0,1]×[0,1] in normalised coords (x is always [0,1])
          const yDelta = y2 - y1;
          let tStart = 0;
          let tEnd = 1;
          if (yDelta !== 0) {
            if (y1 < 0) tStart = Math.max(tStart, -y1 / yDelta);
            if (y1 > 1) tStart = Math.max(tStart, (1 - y1) / yDelta);
            if (y2 > 1) tEnd = Math.min(tEnd, (1 - y1) / yDelta);
            if (y2 < 0) tEnd = Math.min(tEnd, -y1 / yDelta);
          }
          if (tStart < tEnd) {
            const tLabel = tStart + 0.9 * (tEnd - tStart);
            const xLabel = x1 + tLabel * (x2 - x1);
            const yLabel = y1 + tLabel * yDelta;
            const slopeNorm =
              (leaderCurrent.current.speedSt * scales.timeRange) / 1000 / scales.offsetRange;
            const lineAngleDeg = Math.atan2(-slopeNorm * GC_INNER_H, GC_INNER_W) * (180 / Math.PI);
            texts.push({
              x: xLabel,
              y: yLabel,
              content: `${labelFormatVelocity(unitAwareVelocity(3.6 * leaderCurrent.current.speedSt, unitType), unitType, 1)}`,
              color: colors.leader,
              orientation: 'horizontal',
              angle: lineAngleDeg,
            });
          }
        }

        // the window within average speed is calculated, as a shaded rectangle
        rects.push({
          x: scales.toX(timeInMs - avgSpeedWindowSeconds * 1000),
          y: 0,
          width: (avgSpeedWindowSeconds * 1000) / scales.timeRange,
          height: scales.toY(leaderCurrent.current.fromStart),
          fillColor: `${colors.leader}4d`,
        });
      }
      for (const p of rawLeaderPoints) {
        if (p.time < scales.minTime) continue;
        dots.push({
          time: scales.toX(p.time),
          offset: scales.toY(p.offset),
          color: colors.leader,
          type: 'leader',
        });
      }
    }
  }

  const handleDownload = useCallback(() => {
    const node = svgRef.current;
    if (node == null) return;
    const clone = node.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('width', String(GC_W * 2));
    clone.setAttribute('height', String(GC_H * 2));
    clone.style.cssText = '';
    clone.querySelector(`rect[width="${GC_W}"][height="${GC_H}"]`)?.remove();
    let xml = new XMLSerializer().serializeToString(clone);
    for (const [night, day] of NIGHT_TO_DAY) xml = xml.replaceAll(night, day);
    const svgString = `<?xml version="1.0" encoding="utf-8"?>\n${xml}`;
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const safeName = eventName
      ? `gap-chart-${eventName
          .replace(/[/\\:*?"<>|]+/g, '_')
          .replace(/_+/g, '_')
          .trim()}.svg`
      : 'gap-chart.svg';
    downloadBlob(blob, safeName);
  }, [eventName, GC_W]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${GC_W} ${GC_H}`}
        preserveAspectRatio="xMinYMid meet"
        style={{ width: '100%', height: '100%', display: 'block' }}
        role="img"
        aria-labelledby="gc-title"
      >
        <title id="gc-title">Gaps chart — participant and leader offset over time</title>
        {/* Background */}
        <rect x={0} y={0} width={GC_W} height={GC_H} fill={bgFill} />
        <defs>
          <marker id="gc-arrow" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto">
            <polygon points="0 0, 7 2.5, 0 5" fill={axisStroke} />
          </marker>
          <clipPath id="gc-plot-area">
            <rect x={GC_PAD_LEFT} y={GC_PAD_TOP} width={GC_INNER_W} height={GC_INNER_H} />
          </clipPath>
        </defs>

        {/* ── Structural chrome – always visible ── */}
        {/* Y-axis with arrow */}
        <line
          x1={GC_PAD_LEFT}
          y1={GC_PAD_TOP + GC_INNER_H}
          x2={GC_PAD_LEFT}
          y2={GC_PAD_TOP - 8}
          stroke={axisStroke}
          strokeWidth={1}
          markerEnd="url(#gc-arrow)"
        />
        {/* X-axis with arrow */}
        <line
          x1={GC_PAD_LEFT}
          y1={GC_PAD_TOP + GC_INNER_H}
          x2={GC_PAD_LEFT + GC_INNER_W + 8}
          y2={GC_PAD_TOP + GC_INNER_H}
          stroke={axisStroke}
          strokeWidth={1}
          markerEnd="url(#gc-arrow)"
        />
        {/* Y-axis tick marks in pixel space – outside clip path so they extend into padding */}
        {Array.from({ length: Y_TICK_COUNT + 1 }, (_, i) => {
          const ny = i / Y_TICK_COUNT;
          const py = GC_PAD_TOP + (1 - ny) * GC_INNER_H;
          return (
            <line
              key={ny}
              x1={GC_PAD_LEFT}
              y1={py}
              x2={GC_PAD_LEFT - 4}
              y2={py}
              stroke={tickStroke}
              strokeWidth={1}
            />
          );
        })}
        {/* X-axis tick marks in pixel space */}
        {Array.from({ length: X_TICK_COUNT + 1 }, (_, i) => {
          const nx = i / X_TICK_COUNT;
          const px = GC_PAD_LEFT + nx * GC_INNER_W;
          return (
            <line
              key={nx}
              x1={px}
              y1={GC_PAD_TOP + GC_INNER_H}
              x2={px}
              y2={GC_PAD_TOP + GC_INNER_H + 4}
              stroke={tickStroke}
              strokeWidth={1}
            />
          );
        })}
        {/* Y-axis label */}
        <text
          transform={`translate(${GC_PAD_LEFT - 4}, ${GC_PAD_TOP + 26}) rotate(-90)`}
          textAnchor="middle"
          fontSize={9}
          fill={labelFill}
        >
          offset
        </text>
        {/* X-axis label */}
        <text
          x={GC_PAD_LEFT + GC_INNER_W - 14}
          y={GC_H - 42}
          textAnchor="middle"
          fontSize={9}
          fill={labelFill}
        >
          time
        </text>

        {/* No-data notice */}
        {scales == null && (
          <text
            x={GC_PAD_LEFT + GC_INNER_W / 2}
            y={GC_PAD_TOP + GC_INNER_H / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={10}
            fill={noDataFill}
          >
            No data - fetch or hover
          </text>
        )}

        {/*
         * ── Data layer ──────────────────────────────────────────────────────────
         * transform maps unit [0,1]×[0,1] → pixel plot area.
         * origin (0,0) = plot bottom-left; x increases right; y increases upward.
         * All geometry inside uses normalised coordinates; SVG handles the scaling.
         */}
        <g clipPath="url(#gc-plot-area)">
          <g transform={DATA_TRANSFORM}>
            {rects.map((r, i) => (
              <rect
                key={`${r.x}-${r.y}-${i}`}
                x={r.x}
                y={r.y}
                width={r.width}
                height={r.height}
                fill={r.fillColor}
              />
            ))}
            {lines.map((l, i) => (
              <line
                key={`${l.x1}-${l.x2}-${i}`}
                x1={l.x1}
                y1={l.y1}
                x2={l.x2}
                y2={l.y2}
                stroke={l.color}
                strokeWidth={l.width ?? 1}
                vectorEffect="non-scaling-stroke"
                strokeDasharray={l.striped ? '4 2' : undefined}
              />
            ))}
          </g>
        </g>

        {/* Dots in pixel space – rendered outside DATA_TRANSFORM to avoid non-uniform scale distorting circles */}
        <g clipPath="url(#gc-plot-area)">
          {dots.toReversed().map((d, i) => (
            <circle
              key={`${d.type}-${d.time}-${i}`}
              cx={GC_PAD_LEFT + d.time * GC_INNER_W}
              cy={GC_PAD_TOP + (1 - d.offset) * GC_INNER_H}
              r={d.isBig ? 3.2 : 2.6}
              fill={d.color}
              stroke={d.strokeColor}
              strokeWidth={d.strokeColor != null ? 1.0 : 0}
            />
          ))}
        </g>

        {/* Data texts in pixel space – coordinates converted from normalised */}
        <g clipPath="url(#gc-plot-area)">
          {texts.map((t, i) => {
            const px = GC_PAD_LEFT + t.x * GC_INNER_W - (t.orientation === 'vertical' ? 2 : 0);
            const py = GC_PAD_TOP + (1 - t.y) * GC_INNER_H - (t.orientation === 'vertical' ? 0 : 2);
            return (
              <text
                key={`${t.x}-${t.y}-${i}`}
                x={px}
                y={py}
                fill={t.color}
                fontSize={8}
                dominantBaseline="bottom"
                textAnchor="middle"
                transform={
                  t.angle != null
                    ? `rotate(${t.angle}, ${px}, ${py})`
                    : t.orientation === 'vertical'
                      ? `rotate(-90, ${px}, ${py})`
                      : undefined
                }
              >
                {t.content}
              </text>
            );
          })}
        </g>

        {/* Tick labels in pixel space – only when data is available */}
        {scales != null && (
          <>
            {/* Y-axis tick labels */}
            {Array.from({ length: Y_TICK_COUNT + 1 }, (_, i) => {
              const ny = i / Y_TICK_COUNT;
              const val = scales.minOffset + ny * (scales.maxOffset - scales.minOffset);
              const pixelY = GC_PAD_TOP + (1 - ny) * GC_INNER_H;
              return (
                <text
                  key={ny}
                  x={GC_PAD_LEFT - 6}
                  y={pixelY + 4}
                  textAnchor="end"
                  fontSize={8}
                  fill={labelFill}
                >
                  {labelFormatDistance(val, unitType, 1)}
                </text>
              );
            })}
            {/* X-axis tick labels */}
            {Array.from({ length: X_TICK_COUNT + 1 }, (_, i) => {
              const nx = i / X_TICK_COUNT;
              const ms = scales.minTime + nx * (scales.maxTime - scales.minTime);
              const pixelX = GC_PAD_LEFT + nx * GC_INNER_W;
              const labelY = GC_PAD_TOP + GC_INNER_H + 16;
              return (
                <text
                  key={nx}
                  x={pixelX}
                  y={labelY}
                  textAnchor="middle"
                  fontSize={8}
                  fill={labelFill}
                  transform={`rotate(-30, ${pixelX}, ${labelY})`}
                >
                  {formatHHMMSS(ms)}
                </text>
              );
            })}
          </>
        )}
      </svg>
      {hovered && (
        <button
          type="button"
          onClick={handleDownload}
          title="Download SVG"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: 'rgba(0,0,0,0.45)',
            border: 'none',
            borderRadius: 3,
            color: '#fff',
            cursor: 'pointer',
            fontSize: 11,
            padding: '2px 6px',
            lineHeight: '18px',
          }}
        >
          ↓ SVG
        </button>
      )}
    </div>
  );
};
